/**
 * FilterTaksasi Component
 * 
 * Filter component for Taksasi data with date range, estate, division, and pagination
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Input,
  Button,
  Text,
  Flex,
  Grid,
  useTheme,
  Select,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { MdClear, MdRefresh } from 'react-icons/md';
import { FiSearch, FiDownload } from 'react-icons/fi';
import api from '../../../auth/apiClient';
import { TaksasiFilters } from '../types';
import { TAKSASI_DEFAULTS } from '../apiConfig';

interface Company {
  id: number;
  name: string;
}

interface Estate {
  id: number;
  name: string;
  odoo_id: number;
  company_id?: number;
}

interface Division {
  id: number;
  name: string;
  estate_id: number;
  odoo_id: number;
}

interface FilterTaksasiProps {
  filters: TaksasiFilters;
  onFilterChange: (filters: TaksasiFilters) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  onExport?: (filters?: TaksasiFilters) => void;
  onRefresh?: () => void;
}

const FilterTaksasi: React.FC<FilterTaksasiProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  onExport,
  onRefresh,
}) => {
  const theme = useTheme();
  const [localFilters, setLocalFilters] = useState<TaksasiFilters>(filters);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [estates, setEstates] = useState<Estate[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [allDivisions, setAllDivisions] = useState<Division[]>([]);

  const filteredEstates = React.useMemo(() => {
    if (!localFilters.company_id) return estates;
    return estates.filter((estate) => estate.company_id === localFilters.company_id);
  }, [estates, localFilters.company_id]);

  // Get default date range (last 7 days)
  const getDefaultDateRange = () => {
    const today = new Date();
    // const lastWeek = new Date(today);
    // lastWeek.setDate(today.getDate() - 7);

    return {
      date_from: today.toISOString().split('T')[0],
      date_to: today.toISOString().split('T')[0],
    };
  };

  // Style constants
  const inputStyles = {
    size: 'sm' as const,
    borderRadius: 'lg',
    borderColor: 'gray.200',
    _hover: { borderColor: 'gray.300' },
    _focus: {
      borderColor: theme.colors.blue[400],
      boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
    },
  };

  // Fetch estates and divisions on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [optionsResponse, estatesDbResponse] = await Promise.all([
          api.get('/inspeksi/options'),
          api.get('/inspeksi/web/estate-db'),
        ]);

        const data = optionsResponse.data;
        const estatesDbData = estatesDbResponse.data;

        const fetchedCompanies = data.companies || [];
        setCompanies(fetchedCompanies);
        setAllDivisions(data.divisis || []);

        const mergedEstates: Estate[] = (estatesDbData || []).map((estateDb: any) => {
          const optionsEstate = (data.estates || []).find((est: any) => est.name === estateDb.name);
          return {
            id: estateDb.id,
            name: estateDb.name,
            company_id: estateDb.company_id,
            odoo_id: optionsEstate?.odoo_id || estateDb.id,
          };
        });

        setEstates(mergedEstates);

        // Auto-set first company if not already set, then auto-apply filter
        if (fetchedCompanies.length > 0 && !localFilters.company_id) {
          const defaultCompanyId = fetchedCompanies[0].id;
          const autoFilters = {
            ...localFilters,
            company_id: defaultCompanyId
          };

          // Sync local + parent state — fetchData akan otomatis terpanggil
          // melalui setFilters → useEffect di useTaksasi, tanpa perlu onApplyFilters
          setLocalFilters(autoFilters);
          onFilterChange(autoFilters);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Filter divisions when estate changes
  useEffect(() => {
    if (!localFilters.estate_id) {
      setDivisions([]);
      return;
    }

    // Find selected estate to get its id for filtering divisions
    const selectedEstate = estates.find(e => e.odoo_id === localFilters.estate_id);
    if (selectedEstate) {
      const filteredDivisions = allDivisions.filter(
        (div: Division) => div.estate_id === selectedEstate.id
      );
      setDivisions(filteredDivisions);
    }
  }, [localFilters.estate_id, estates, allDivisions]);

  // Sync local filters when filters prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleInputChange = useCallback(
    (field: keyof TaksasiFilters, value: string | number | undefined | null) => {
      const newFilters = { ...localFilters };

      // Handle estate change - reset division when estate changes
      if (field === 'estate_id') {
        newFilters.estate_id = value ? Number(value) : undefined;
        newFilters.division_id = undefined; // Reset division when estate changes
      } else if (field === 'company_id') {
        newFilters.company_id = value ? Number(value) : 1;
        newFilters.estate_id = undefined;
        newFilters.division_id = undefined;
      } else if (field === 'division_id') {
        newFilters.division_id = value ? Number(value) : undefined;
      } else if (field === 'limit') {
        newFilters.limit = value ? Number(value) : TAKSASI_DEFAULTS.LIMIT;
      } else {
        (newFilters as any)[field] = value;
      }

      setLocalFilters(newFilters);
    },
    [localFilters]
  );

  const handleApplyFilters = useCallback(() => {
    onFilterChange(localFilters);
    onApplyFilters();
  }, [localFilters, onFilterChange, onApplyFilters]);

  const handleClearFilters = useCallback(() => {
    const defaults = getDefaultDateRange();
    const clearedFilters: TaksasiFilters = {
      date_from: defaults.date_from,
      date_to: defaults.date_to,
      company_id: 1,
      estate_id: undefined,
      division_id: undefined,
      page: TAKSASI_DEFAULTS.PAGE,
      limit: TAKSASI_DEFAULTS.LIMIT,
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onClearFilters();
  }, [onFilterChange, onClearFilters]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 6 }}
      borderRadius="xl"
      boxShadow="sm"
      border="1px"
      borderColor="gray.100"
    >
      <VStack spacing={4} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="gray.700">
            Filter Data Taksasi
          </Text>
          <HStack spacing={2}>
            {onRefresh && (
              <Tooltip label="Refresh Data" hasArrow>
                <IconButton
                  aria-label="Refresh data"
                  icon={<MdRefresh />}
                  size="sm"
                  variant="ghost"
                  onClick={onRefresh}
                  colorScheme="blue"
                  border="1px solid"
                  borderColor="blue.200"
                  _hover={{
                    borderColor: "blue.500",
                    bg: "blue.50"
                  }}
                />
              </Tooltip>


            )}
            {onExport && (
              <Tooltip
                label={!localFilters.company_id ? "Pilih Company untuk export data" : "Export ke Excel"}
                hasArrow
              >
                <Box display="inline-block">
                  <Button
                    rightIcon={<FiDownload />}
                    size="sm"
                    variant="ghost"
                    onClick={() => onExport?.(localFilters)}
                    isDisabled={!localFilters.company_id}
                    colorScheme="green"
                    border="1px solid"
                    borderColor="green.200"
                    _hover={!localFilters.company_id ? undefined : {
                      borderColor: "green.500",
                      bg: "green.50"
                    }}
                  >
                    Excel
                  </Button>
                </Box>
              </Tooltip>
            )}
          </HStack>
        </Flex>

        {/* Filter Fields */}
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(5, 1fr)',
          }}
          gap={4}
        >
          {/* Date From */}
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600" mb={1}>
              Tanggal Mulai
            </FormLabel>
            <Input
              {...inputStyles}
              type="date"
              value={localFilters.date_from || ''}
              onChange={(e) => handleInputChange('date_from', e.target.value)}
              onKeyDown={handleKeyPress}
            />
          </FormControl>

          {/* Date To */}
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600" mb={1}>
              Tanggal Akhir
            </FormLabel>
            <Input
              {...inputStyles}
              type="date"
              value={localFilters.date_to || ''}
              onChange={(e) => handleInputChange('date_to', e.target.value)}
              onKeyDown={handleKeyPress}
            />
          </FormControl>

          {/* Company ID */}
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600" mb={1}>
              Company
            </FormLabel>
            <Select
              {...inputStyles}
              // placeholder="Semua Company"
              value={localFilters.company_id || ''}
              onChange={(e) => handleInputChange('company_id', e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Estate ID */}
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600" mb={1}>
              Estate
            </FormLabel>
            <Select
              {...inputStyles}
              placeholder="Semua Estate"
              value={localFilters.estate_id || ''}
              onChange={(e) => handleInputChange('estate_id', e.target.value)}
            >
              {filteredEstates.map((estate) => (
                <option key={estate.id} value={estate.odoo_id}>
                  {estate.name}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Division ID */}
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600" mb={1}>
              Divisi
            </FormLabel>
            <Select
              {...inputStyles}
              placeholder="Semua Divisi"
              value={localFilters.division_id || ''}
              onChange={(e) => handleInputChange('division_id', e.target.value)}
              isDisabled={!localFilters.estate_id || divisions.length === 0}
            >
              {divisions.map((division) => (
                <option key={division.id} value={division.odoo_id}>
                  {division.name}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Items Per Page */}

        </Grid>

        {/* Action Buttons */}
        <Flex
          gap={2}
          justify={{ base: 'stretch', md: 'flex-end' }}
          direction={{ base: 'column', sm: 'row' }}
        >
          <Button
            leftIcon={<MdClear />}
            onClick={handleClearFilters}
            size="sm"
            variant="outline"
            colorScheme="red"
            borderRadius="lg"
            flex={{ base: 1, sm: 'initial' }}
          >
            Reset
          </Button>
          <Button
            leftIcon={<FiSearch />}
            onClick={handleApplyFilters}
            size="sm"
            colorScheme="blue"
            borderRadius="lg"
            flex={{ base: 1, sm: 'initial' }}
          >
            Terapkan Filter
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default FilterTaksasi;
